import { DatePipe, PercentPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  BusinessDay,
  CalculateFutureDatePayload,
  ExpirationSettlementDate,
  Leg
} from '@approot/models';
import {
  AdminService,
  LegService,
  LegSyncService,
  PricingExtraService,
  ProductsConfigService,
  RfqService,
  RfqTabService,
  TradesCaptureService
} from '@approot/services';
import {
  DateUtilities,
  Utilities,
  crossToPair,
  reformatDate
} from '@approot/utilities';
import dayjs from 'dayjs';
import { firstValueFrom } from 'rxjs';
import {
  calculateFutureDateMarkPending,
  handleCalculateFutureDateForRate
} from '../utilities/date-utilities';
import {
  getAttributeControlFromKey,
  removeDateLag
} from '../utilities/leg-attribute-utilities';
import {
  clearCustomText,
  clearTextColor,
  markFieldAsPending,
  payLoadDate,
  removePendingFromField,
  setBracketedAttributeInfo
} from '../utilities/leg.utilities';
import { LegFwdVolService } from './leg-fwd-vol.service';
import { LegHelperService } from './leg-helper.service';
import { LegScheduleService } from './leg-schedule.service';
import { LegSpotStrikeService } from './leg-spot-strike.service';

export interface DateControl {
  formControl: AbstractControl;
  setFormControl: boolean;
  controlName: string;
  useDate?: boolean;
}

export interface NextBusinessDayPayload {
  dateOrLag: string;
  currencyOrCalendar: string;
}
@Injectable({
  providedIn: 'root'
})
export class LegDateService {
  constructor(
    private legHelperService: LegHelperService,
    private legService: LegService,
    private legFwdVolService: LegFwdVolService,
    private legSpotStrikeService: LegSpotStrikeService,
    private legScheduleService: LegScheduleService,
    private legSyncService: LegSyncService,
    private pricingExtraService: PricingExtraService,
    private percentPipe: PercentPipe,
    private datePipe: DatePipe,
    private adminService: AdminService,
    private productsConfigService: ProductsConfigService,
    private rfqService: RfqService,
    private rfqTabService: RfqTabService,
    private tradesCaptureService: TradesCaptureService
  ) {}

  public nextBusinessDay(
    payload: NextBusinessDayPayload,
    controlName: string,
    scheduleNow: boolean,
    placeLagInDate: boolean
  ): void {
    // scheduleNow indicates whether a schedule should be generated.
    // calcs next business day and sets the date in passed control
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    if (form.get(controlName).valid) {
      markFieldAsPending(form, leg, controlName);
      this.legService.nextBusinessDay(payload).subscribe({
        next: (resp: BusinessDay) => {
          this.nextBusinessDayResp(
            resp,
            controlName,
            placeLagInDate,
            payload,
            scheduleNow
          );
        },
        error: () => {
          removePendingFromField(form, leg, rfqExecutionSettings, controlName);
        }
      });
    }
  }

  public premiumSettlementDateChange(value: string): void {
    const legSyncingInfo = this.legHelperService.legSyncingInfo;
    if (
      legSyncingInfo?.premiumSettlementDate &&
      this.legHelperService.presetsLoaded
    ) {
      this.legSyncService.syncPremiumSettlementDate(
        this.legHelperService.legIndex,
        value
      );
    }
  }

  public copySettlementDateToPremiumDate(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const premiumDateCtrl = form.get('premiumSettlementDate');
    const settlementDateCtrl = form.get('settlementDate');
    const attribute = getAttributeControlFromKey(
      'premiumSettlementDate',
      leg.attributesAsFormControls
    );
    if (
      premiumDateCtrl &&
      form.get('premiumValueType')?.value === 'Forward' &&
      settlementDateCtrl
    ) {
      this.premiumSettlementDateChange(settlementDateCtrl.value);
      premiumDateCtrl.setValue(settlementDateCtrl.value, { emitEvent: false });
      attribute.readOnly = false;
    }
  }

  public calculateExpirationSettlementDate(
    primaryFormControl: DateControl,
    secondaryFormControl: DateControl,
    cross: string,
    customPayload?: any // tou can pass in a custom payload the avoids checking for the validity
  ) {
    const form = this.legHelperService.form;
    const dateControlUsed =
      primaryFormControl && primaryFormControl.useDate
        ? primaryFormControl
        : secondaryFormControl; // formControl to use

    // setSettlementDate decides whether the settlement date from the future date service should be set or not
    if (customPayload || dateControlUsed.formControl.valid) {
      // if a custom payload is sent no need to the form control to be valid
      const payload = customPayload
        ? customPayload
        : DateUtilities.dateOrLagToPayload(
            dateControlUsed.formControl.value.toLowerCase(),
            cross,
            this.pricingExtraService.getMarketDataTimeStamp,
            this.productsConfigService.getCutOffCode(form.get('cutOff')?.value),
            this.productsConfigService.getFixingSourceCode(
              form.get('fixingSource')?.value
            )
          );
      if (
        !dateControlUsed ||
        dateControlUsed.controlName !== 'settlementDate'
      ) {
        this.callCalculateFutureDate(
          payload,
          primaryFormControl,
          secondaryFormControl
        );
      } else {
        // if its a settlement date being passed in, then use the next businessDay function
        payload['currencyOrCalendar'] = form.get('cross')
          ? form.get('cross').value
          : '';
        this.nextBusinessDay(payload, dateControlUsed.controlName, true, true); // calc the next businessDay
      }
    }
  }

  private callCalculateFutureDate(
    payload: CalculateFutureDatePayload,
    primaryFormControl: DateControl,
    secondaryFormControl: DateControl
  ) {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const controlToUse =
      primaryFormControl && primaryFormControl.useDate
        ? primaryFormControl
        : secondaryFormControl;

    calculateFutureDateMarkPending(
      form,
      leg,
      primaryFormControl,
      secondaryFormControl,
      payload
    );

    this.legService.calculateFutureDate(payload).then(
      p => {
        p.subscribe(
          (resp: any) => {
            this.handleCallCalculateFutureDateResp(
              resp,
              controlToUse,
              payload,
              primaryFormControl,
              secondaryFormControl
            );
          },
          _ => {
            this.calculateFutureDateError(controlToUse, payload);
          }
        );
      },
      _ => {
        this.calculateFutureDateError(controlToUse, payload);
      }
    );
  }

  public onFirstDateChange(
    val: string,
    firstDateToUse: string,
    frequencyToUse: string
  ): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    removeDateLag(firstDateToUse, leg); // attempt to remove (1m) if its there
    clearCustomText(form);
    if (firstDateToUse !== 'firstExerciseDate') {
      if (!!val) {
        if (
          DateUtilities.isValidLag(val) ||
          (form.get(firstDateToUse).valid &&
            firstDateToUse !== 'firstSettlementDate')
        ) {
          // we need to calc future date if user enters lag
          const payload = DateUtilities.dateOrLagToPayload(
            val.toLowerCase(),
            form.get('cross').value,
            this.pricingExtraService.getMarketDataTimeStamp,
            this.productsConfigService.getCutOffCode(form.get('cutOff')?.value),
            this.productsConfigService.getFixingSourceCode(
              form.get('fixingSource')?.value
            )
          );
          markFieldAsPending(form, leg, firstDateToUse);
          this.legService.calculateFutureDate(payload).then(
            resp => {
              resp.subscribe(
                (dates: ExpirationSettlementDate) => {
                  removePendingFromField(
                    form,
                    leg,
                    rfqExecutionSettings,
                    firstDateToUse
                  );

                  if (
                    firstDateToUse === 'firstSettlementDate' &&
                    dates.settlementDate
                  ) {
                    form
                      .get(firstDateToUse)
                      .patchValue(
                        DateUtilities.dateToPrettyDate(dates.settlementDate),
                        { emitEvent: false }
                      );
                    if (DateUtilities.isValidLag(payload.dateOrLag)) {
                      // if there is a lag, add the lag as a span
                      setBracketedAttributeInfo(
                        firstDateToUse,
                        payload.dateOrLag,
                        leg
                      );
                    }
                  } else if (dates.expirationDate) {
                    form
                      .get(firstDateToUse)
                      .patchValue(
                        DateUtilities.dateToPrettyDate(dates.expirationDate),
                        { emitEvent: false }
                      );

                    if (DateUtilities.isValidLag(payload.dateOrLag)) {
                      // if there is a lag, add the lag as a span
                      setBracketedAttributeInfo(
                        firstDateToUse,
                        payload.dateOrLag,
                        leg
                      );
                    }
                  }
                  this.legScheduleService.callScheduleGenerator();
                },
                () => {
                  removePendingFromField(
                    form,
                    leg,
                    rfqExecutionSettings,
                    firstDateToUse
                  );
                }
              );
            },
            err => {
              removePendingFromField(
                form,
                leg,
                rfqExecutionSettings,
                firstDateToUse
              );
            }
          );
        } else {
          if (form.get(firstDateToUse).valid) {
            const payload = {
              dateOrLag: payLoadDate(val),
              currencyOrCalendar: form.get('cross').value
            }; // we need to get the firstDate to be the next business day of that date
            this.nextBusinessDay(payload, firstDateToUse, true, true); // calc the next businessDay
            // we need to wait for the date control to format/update before calling
            // schedule generator
          }
        }
      }
    } else {
      if (this.legHelperService.presetsLoaded) {
        this.legScheduleService.rateIndexDateAdd({
          startDate: null,
          tenor: 'firstExerciseDate'
        });
      }
    }

    if (form.get(frequencyToUse)?.value === Utilities.customText) {
      form.get(frequencyToUse).setValue('');
      clearTextColor(leg, frequencyToUse); // wipe the custom color if its been set
    }
  }

  // function is used when trying to get the treasury rate, and the expirydate
  // or redemption date from the back end
  public async getDcdExpirationDateAndTreasuryRate(
    primaryFormControl: DateControl,
    secondaryFormControl: DateControl
  ): Promise<void> {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const formControlToUse =
      primaryFormControl && primaryFormControl.useDate
        ? primaryFormControl
        : secondaryFormControl;

    let depositTaker;
    const depositTakerAttribute = leg.attributes.find(
      x => x.shortName === 'depositTaker'
    );

    if (form.value.depositTaker) {
      depositTaker = form.value.depositTaker;
    } else if (
      depositTakerAttribute?.hide &&
      depositTakerAttribute?.selectFirst
    ) {
      depositTaker = await this.getHiddenDepositTaker();
      // form will be invalid which will fail pricing
      // unless setting the depositTaker value
      form.get('depositTaker').setValue(depositTaker);
    }

    if (
      !!depositTaker &&
      !!form.get('depositCurrency')?.value &&
      !!form.get('alternativeCurrency')?.value &&
      !!form.get('startDate')?.valid
    ) {
      const valueDateTime = this.pricingExtraService.getMarketDataTimeStamp
        ? new Date(this.pricingExtraService.getMarketDataTimeStamp).getTime()
        : new Date().getTime();

      const customPayload = {
        // the payload that is going to be sent
        task: 'getDcdExpirationDateAndTreasuryRate',
        product: this.legService.createProductsObject([
          {
            product: form.value,
            attributes: leg.attributes
          }
        ])[0],
        parent: this.adminService.userInfo.groups[0],
        valueDateTime: this.datePipe.transform(
          valueDateTime,
          'yyyy-MM-dd hh:mm:ss'
        ),
        cutOff: this.productsConfigService.getCutOffCode(
          form.get('cutOff')?.value
        ),
        fixingSource: this.productsConfigService.getFixingSourceCode(
          form.get('fixingSource')?.value
        )
      };

      if (formControlToUse.controlName === 'expiryDate') {
        // since we are sending the expiry date hide the redemptionDate
        customPayload.product.redemptionDate = '';
        customPayload.task = 'getDcdRedemptionDateAndTreasuryRate';
      } else {
        // since we are sending the redemption Date hide the expiryDate
        customPayload.product.expiryDate = '';
      }

      // turn the date into the correct format
      if (
        DateUtilities.isDateOfSlashFormat(formControlToUse.formControl.value)
      ) {
        customPayload.product[formControlToUse.controlName] =
          DateUtilities.restructureDate(formControlToUse.formControl.value);
      } else if (
        DateUtilities.isPrettyDate(formControlToUse.formControl.value)
      ) {
        customPayload.product[formControlToUse.controlName] =
          DateUtilities.prettyDateToDate(formControlToUse.formControl.value);
      } else {
        customPayload.product[formControlToUse.controlName] =
          formControlToUse.formControl.value;
      }

      this.calculateExpirationSettlementDate(
        primaryFormControl,
        secondaryFormControl,
        '',
        customPayload
      );
    }
  }

  async getHiddenDepositTaker() {
    const depositTakersAndCurrencies = await firstValueFrom(
      this.tradesCaptureService.depositTakersAndCurrenciesSubject
    );
    return depositTakersAndCurrencies[0].entity;
  }

  public onRedemptionDateChange(val: string): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    removeDateLag('redemptionDate', leg); // attempt to remove (1m) if its there
    if (form.get('treasuryRate')) {
      form.get('treasuryRate').setValue('');
    }

    if (
      form.get('redemptionDate').valid &&
      form.get('depositCurrency').value &&
      form.get('alternativeCurrency').value
    ) {
      // rule: this is for Dual-currency Deposit
      if (DateUtilities.isDateOfSlashFormat(val)) {
        // if the date is of a slashed format
        val = DateUtilities.restructureDate(val);
        DateUtilities.onDateChanged(form.get('redemptionDate')); // needs to be done to ensure it visually looks good
      }

      const createdCross = this.productsConfigService.getCrossFromCurrencies(
        form.get('depositCurrency').value,
        form.get('alternativeCurrency').value
      );

      if (createdCross) {
        const payload = {
          dateOrLag: val,
          currencyOrCalendar: createdCross
        };

        if (form.get('depositDate')?.value || form.get('startDate')?.value) {
          // if there is a deposit date then make the dateOrLag in reference to that date
          payload['referenceDate'] = DateUtilities.prettyDateToDate(
            form.get('depositDate')
              ? form.get('depositDate').value
              : form.get('startDate').value
          );
        }

        if (leg.productType !== 'newdcd') {
          this.nextBusinessDay(payload, 'redemptionDate', false, true); // calc the next businessDay
        } else if (form.get('redemptionDate').valid) {
          const updateExpiryDate =
            DateUtilities.isValidLag(val) || !form.get('expiryDate')?.value;
          this.getDcdExpirationDateAndTreasuryRate(
            {
              formControl: form.get('redemptionDate'),
              setFormControl: true,
              controlName: 'redemptionDate',
              useDate: true
            },
            {
              formControl: form.get('expiryDate'),
              setFormControl: updateExpiryDate,
              controlName: 'expiryDate',
              useDate: false
            }
          );
        }
      }
    }
  }

  public setPremiumDefaultDate(): void {
    const form = this.legHelperService.form;
    if (
      form.get('premiumPaymentDate')?.enabled &&
      this.legHelperService.presetsLoaded &&
      this.adminService.useDefaults
    ) {
      const crossToUse = this.legHelperService.valueToUseAsCross(true);
      if (crossToUse) {
        const customPayload = DateUtilities.dateOrLagToPayload(
          '0b',
          crossToUse,
          this.pricingExtraService.getMarketDataTimeStamp,
          this.productsConfigService.getCutOffCode(form.get('cutOff')?.value),
          this.productsConfigService.getFixingSourceCode(
            form.get('fixingSource')?.value
          )
        );

        this.calculateExpirationSettlementDate(
          null,
          {
            formControl: form.get('premiumPaymentDate'),
            setFormControl: true,
            controlName: 'premiumPaymentDate',
            useDate: true
          },
          '',
          customPayload
        );
      }
    }
  }

  public xccySwapDateAdd(val: string): void {
    const ctrl = this.legHelperService.form.get('xccySwapEndDate');
    if (DateUtilities.isValidLag(val)) {
      ctrl.setErrors(null);
      const payload = {
        task: 'xccySwapDateAdd',
        tenor: val,
        date: '2025-05-13',
        currencyPair: this.legHelperService.form.get('cross').value
      };

      this.legService
        .dateService(payload)
        .subscribe((resp: { date: string }) => {
          const date = reformatDate(resp.date, 'D MMM YYYY');
          ctrl.setValue(date, { emitEvent: false });
          this.legScheduleService.xccySwapGetSchedules({
            xccySwapEndDate: date
          });
          ctrl.setErrors(null);
        });
    } else if (ctrl.valid) {
      this.legScheduleService.xccySwapGetSchedules({
        xccySwapEndDate: reformatDate(val, 'YYYY-MM-DD')
      });
    }
  }

  public calculateExpirationSettlementDateCurrencyOnly(
    primaryFormControl: DateControl,
    secondaryFormControl: DateControl
  ): void {
    const form = this.legHelperService.form;
    const controlFormToUse =
      primaryFormControl && primaryFormControl.useDate
        ? primaryFormControl
        : secondaryFormControl; // the form control that is being used
    if (controlFormToUse.formControl.valid) {
      const payload = DateUtilities.dateOrLagToPayload(
        controlFormToUse.formControl.value.toLowerCase(),
        form.get('currency').value,
        this.pricingExtraService.getMarketDataTimeStamp,
        this.productsConfigService.getCutOffCode(form.get('cutOff')?.value),
        this.productsConfigService.getFixingSourceCode(
          form.get('fixingSource')?.value
        )
      );
      this.callCalculateFutureDate(
        payload,
        primaryFormControl,
        secondaryFormControl
      );
    }
  }

  public calculateExpirationDateFromSettlementDates(
    cross: string,
    date: string[],
    formControl: AbstractControl
  ): void {
    // gets the expiration date from settlement dates
    const payload = {
      currencyPair: cross,
      settlementDates: date
    };
    this.legService
      .calculateExpirationDateFromSettlementDates(payload)
      .then(resp => {
        // next work out the expiration date from the settlement date
        resp.subscribe(
          (resp: { expirationDate: string; settlementDate: string }[]) => {
            formControl.patchValue(
              DateUtilities.dateToPrettyDate(resp[0].expirationDate),
              { emitEvent: false }
            );
          }
        );
      });
  }

  public setPremiumDateFromSpotDate() {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const crossCtrl = form.get('cross');
    if (
      rfqExecutionSettings.directRfq &&
      crossCtrl?.value &&
      form.get('premiumDate') &&
      form.get('premiumType')?.value === 'Spot'
    ) {
      const cross = crossCtrl.value;
      if (cross) {
        markFieldAsPending(form, leg, 'premiumDate');
        this.rfqService
          .calculateSpotDate({
            currentDate: dayjs().utc().startOf('day').valueOf(),
            quotePair: crossToPair(cross)
          })
          .subscribe({
            next: response =>
              form
                .get('premiumDate')
                .setValue(DateUtilities.dateToPrettyDate(response.result)),
            complete: () =>
              removePendingFromField(
                form,
                leg,
                rfqExecutionSettings,
                'premiumDate'
              )
          });
      } else {
        form.get('premiumDate').setValue('');
      }
    }
  }

  public defaultNextBusinessDay(formControlName: string): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const payload = {
      dateOrLag: '0d',
      currencyPair: form.get('cross') ? form.get('cross').value : '',
      currencyOrCalendar: form.get('cross') ? form.get('cross').value : '',
      referenceDate: this.pricingExtraService.getMarketDataTimeStamp
        ? this.pricingExtraService.getMarketDataTimeStamp
        : DateUtilities.getDateInISOFormat()
    };

    if (
      (!leg.preSetPosition ||
        this.legHelperService.presetsLoaded ||
        !leg.preSetPosition['settlementDate']) &&
      leg.productType === 'spot'
    ) {
      this.nextBusinessDay(payload, formControlName, false, false); // calc the next businessDay
    }
  }

  public premiumValueTypeSpotDateCall(val: 'Spot' | 'Forward'): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const legSyncingInfo = this.legHelperService.legSyncingInfo;
    const attribute = getAttributeControlFromKey(
      'premiumSettlementDate',
      leg.attributesAsFormControls
    );
    if (attribute) {
      const cross = form.get('cross').value;
      markFieldAsPending(form, leg, 'premiumSettlementDate');
      this.rfqService
        .calculateSpotDate({
          currentDate: dayjs().utc().startOf('day').valueOf(),
          quotePair: crossToPair(cross)
        })
        .subscribe({
          next: response => {
            const v = reformatDate(response.result, 'D MMM YYYY');
            form.get('premiumSettlementDate').setValue(v, { emitEvent: false });
            if (legSyncingInfo?.premiumSettlementDate) {
              this.premiumSettlementDateChange(v);
            }
            this.legHelperService.onChangeInLegFn();
          },
          complete: () => {
            removePendingFromField(
              form,
              leg,
              rfqExecutionSettings,
              'premiumSettlementDate'
            );
            const isSpot = val === 'Spot';
            attribute.readOnly = isSpot;
          }
        });
    }
  }

  public async calculateFuturePremiumSettlementDate(): Promise<void> {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const field = `premiumSettlementDate`;
    const ctrl = form.get(field);
    this.premiumSettlementDateChange(ctrl.value);
    if (!ctrl || !/^\d+[bdmwy]/i.test(ctrl.value)) {
      return;
    }
    markFieldAsPending(form, leg, field);
    const dateOrLag = reformatDate(ctrl.value, 'YYYY-MM-DD');
    const referenceDate = reformatDate(new Date(), 'YYYY-MM-DD');
    const currencyPair = this.rfqTabService.getCurrencyPair();
    const cutOff = this.productsConfigService.getCutOffCode(
      form.get('cutOff')?.value
    );
    const fixingSource = this.productsConfigService.getFixingSourceCode(
      form.get('fixingSource')?.value
    );

    // calculateFutureDate is Promise<Observable<any>>
    const obs = await this.legService.calculateFutureDate({
      currencyPair,
      dateOrLag,
      referenceDate,
      cutOff,
      fixingSource
    });
    obs.subscribe({
      next: data => {
        const date = reformatDate(data.settlementDate, 'D MMM YYYY');
        ctrl.setValue(date, { emitEvent: false });
        this.premiumSettlementDateChange(date);
      },
      complete: () => {
        removePendingFromField(form, leg, rfqExecutionSettings, field);
      }
    });
  }

  private nextBusinessDayResp(
    resp: BusinessDay,
    controlName: string,
    placeLagInDate: boolean,
    payload: NextBusinessDayPayload,
    scheduleNow: boolean
  ): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;

    removePendingFromField(form, leg, rfqExecutionSettings, controlName);
    if (resp.nextBusinessDay) {
      form
        .get(controlName)
        .setValue(DateUtilities.dateToPrettyDate(resp.nextBusinessDay), {
          emitEvent: false
        });

      if (placeLagInDate && DateUtilities.isValidLag(payload.dateOrLag)) {
        setBracketedAttributeInfo(controlName, payload.dateOrLag, leg);
      }

      if (leg.productType === 'spot' && controlName === 'settlementDate') {
        // if this doesn't happen then the formControl will be valid
        // but the date element will still show as invalid
        form.get('settlementDate').setErrors(null);
      }

      if (scheduleNow && form.get('schedule')) {
        // if we want to schedule fixings and
        // if there is a schedule field attempt to create it with this new firstFixingDate
        this.legScheduleService.callScheduleGenerator();
      }

      if (
        controlName === 'startDate' &&
        form.get('redemptionDate') &&
        form.get('expiryDate')
      ) {
        // if we are using the start date then enable the redemption date and expirydate
        form.get('redemptionDate').enable();
        form.get('expiryDate').enable();
      }

      if (controlName === 'expiryDate') {
        this.legFwdVolService.initForwardPointVolCalls();
      }
      this.legSpotStrikeService.onStrikeCalc('', leg.shortcutValueUsed.strike); // convert the strike special values into a number
      this.legSpotStrikeService.onStrikeCalc(
        '1',
        leg.shortcutValueUsed.strike1
      );
      this.legSpotStrikeService.onStrikeCalc(
        '2',
        leg.shortcutValueUsed.strike2
      );
    } else {
      console.log('failed to get the next businessDay');
    }
  }

  private reCalculateStrikes(): void {
    const leg = this.legHelperService.leg;
    this.legSpotStrikeService.onStrikeCalc('', leg.shortcutValueUsed.strike); // convert the strike special values into a number
    this.legSpotStrikeService.onStrikeCalc('1', leg.shortcutValueUsed.strike1);
    this.legSpotStrikeService.onStrikeCalc('2', leg.shortcutValueUsed.strike2);
  }

  private setPrimaryDate(
    primaryFormControl: DateControl,
    firstDate: string,
    payload: CalculateFutureDatePayload,
    controlToUse: DateControl
  ): void {
    const leg = this.legHelperService.leg;
    if (primaryFormControl?.setFormControl && firstDate) {
      // if we are setting the first form control
      primaryFormControl.formControl.patchValue(
        DateUtilities.dateToPrettyDate(firstDate),
        { emitEvent: false }
      );
      primaryFormControl.formControl.setErrors(null);
      if (
        primaryFormControl?.setFormControl &&
        primaryFormControl?.useDate &&
        DateUtilities.isValidLag(payload.dateOrLag)
      ) {
        // set the date lag in the textbox
        setBracketedAttributeInfo(
          controlToUse.controlName,
          payload.dateOrLag,
          leg
        );
      }
    }
  }

  private setSecondaryDate(
    secondaryFormControl: DateControl,
    secondDate,
    payload,
    leg: Leg,
    controlToUse: DateControl
  ): void {
    if (secondaryFormControl?.setFormControl && secondDate) {
      // if we are setting the second from control
      // if we should also set the settlement date
      secondaryFormControl.formControl.patchValue(
        DateUtilities.dateToPrettyDate(secondDate),
        { emitEvent: false }
      );

      this.copySettlementDateToPremiumDate();
      secondaryFormControl.formControl.setErrors(null);

      if (
        secondaryFormControl?.useDate &&
        secondaryFormControl?.setFormControl &&
        payload.dateOrLag &&
        DateUtilities.isValidLag(payload.dateOrLag)
      ) {
        // set the date lag in the textbox
        setBracketedAttributeInfo(
          controlToUse.controlName,
          payload.dateOrLag,
          leg
        );
      }
    }
  }

  private calculateFutureDateError(
    controlToUse: DateControl,
    payload: any
  ): void {
    // on failure hide the spinners
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    removePendingFromField(
      form,
      leg,
      rfqExecutionSettings,
      controlToUse.controlName
    );
    if (
      payload.task === 'getDcdRedemptionDateAndTreasuryRate' ||
      payload.task === 'getDcdExpirationDateAnsTreasuryRate'
    ) {
      removePendingFromField(form, leg, rfqExecutionSettings, 'treasuryRate');
    }
  }

  private handleCallCalculateFutureDateResp(
    resp: any,
    controlToUse: DateControl,
    payload: CalculateFutureDatePayload,
    primaryFormControl: DateControl,
    secondaryFormControl: DateControl
  ): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    removePendingFromField(
      form,
      leg,
      rfqExecutionSettings,
      controlToUse.controlName
    );

    handleCalculateFutureDateForRate(payload, form, leg, rfqExecutionSettings);

    const firstDate = resp.expirationDate || resp.redemptionDate;
    const secondDate = resp.settlementDate || resp.expiryDate;
    if (firstDate || secondDate) {
      this.setPrimaryDate(primaryFormControl, firstDate, payload, controlToUse);

      this.setSecondaryDate(
        secondaryFormControl,
        secondDate,
        payload,
        leg,
        controlToUse
      );
      // get fixings if expiry date in past

      if (
        controlToUse.controlName === 'startDate' &&
        form.get('redemptionDate') &&
        form.get('expiryDate')
      ) {
        // these should be disabled before so enable them
        form.get('redemptionDate').enable();
        form.get('expiryDate').enable();
      }

      if (resp.treasuryRate != null && form.get('treasuryRate')) {
        // if treasuryRate is returned set the value
        form
          .get('treasuryRate')
          .setValue(this.percentPipe.transform(resp.treasuryRate, '1.4-4'));
      }

      if (controlToUse.controlName === 'expiryDate') {
        this.legFwdVolService.syncForwardPoints();
        this.legFwdVolService.initForwardPointVolCalls();
      }
      this.reCalculateStrikes();
    } else {
      console.error('Unable to retrieve settlement date.');
    }
  }
}
